import { useMemo, useState } from "react";
import { BarChart3, ChevronDown, Sparkles, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate } from "@/lib/format";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Separator } from "@/components/ui/separator";
import { DateRangeFilter, EmptyState, ErrorState, Eyebrow, LoadingState } from "@/components/PageUi";
import { cn } from "@/lib/utils";

type SpendSummaryResponse = components["schemas"]["SpendSummaryResponse"];
type SpendTotalsRow = components["schemas"]["SpendTotalsRow"];
type SpendCategoryRow = components["schemas"]["SpendCategoryRow"];
type SpendSupplierRow = components["schemas"]["SpendSupplierRow"];
type SpendTagRow = components["schemas"]["SpendTagRow"];
type SpendDeltaRow = components["schemas"]["SpendDeltaRow"];
type ResolvedCategory = components["schemas"]["ResolvedCategoryResponse"];

/** Dolfin's first-class AI/Intelligence category key — broken out visually. */
const AI_CATEGORY_KEY = "ai_intelligence";

const num = (v: number | string | null | undefined): number =>
  typeof v === "number" ? v : v == null ? 0 : Number(v) || 0;

/** ISO `YYYY-MM-DD` for a Date, in UTC. */
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The last full calendar month as `[from, to)`: on 9 Jun 2026 → 2026-05-01 … 2026-06-01. */
function lastFullMonth(): { from: string; to: string } {
  const now = new Date();
  const firstOfThisMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const firstOfPrevMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return { from: isoDate(firstOfPrevMonth), to: isoDate(firstOfThisMonth) };
}

/** `to` still in the future: a month-to-date figure against a full prior window would read as a false drop. */
function isPartialPeriod(to: string | undefined): boolean {
  if (!to) return false;
  const t = new Date(to).getTime();
  return !isNaN(t) && t > Date.now();
}

function pct(fraction: number | string | null | undefined): number | null {
  if (fraction == null) return null;
  const n = typeof fraction === "string" ? parseFloat(fraction) : fraction;
  return isNaN(n) ? null : Math.round(n * 100);
}

export interface SpendSummaryCardProps {
  /** Drill-through to the (capped-at-50) bill ids behind a row; omitted, rows aren't expandable. */
  onBillClick?: (billId: string) => void;
  className?: string;
}

/** Per-currency throughout: never summed across currencies, `defaultCurrency` first. */
export function SpendSummaryCard({ onBillClick, className }: SpendSummaryCardProps) {
  const api = useDolfinClient();
  const initial = useMemo(lastFullMonth, []);
  const [from, setFrom] = useState<string | null>(initial.from);
  const [to, setTo] = useState<string | null>(initial.to);
  const [summary, setSummary] = useState<SpendSummaryResponse | null>(null);
  const [categories, setCategories] = useState<ResolvedCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoryName = useMemo(() => {
    const byKey = new Map(categories.map((c) => [c.key, c.displayName]));
    return (key: string | null | undefined): string =>
      key == null ? "Uncategorised" : byKey.get(key) ?? key;
  }, [categories]);

  async function generate() {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, categoriesRes] = await Promise.all([
        api.GET("/v1/payables/spend-summary", {
          params: { query: { from: from ?? undefined, to: to ?? undefined } },
        }),
        api.GET("/v1/payables/categories"),
      ]);
      if (summaryRes.error || !summaryRes.data) {
        setError(apiErrorMessage(summaryRes.error, "Failed to load spend summary"));
        setSummary(null);
        return;
      }
      if (!categoriesRes.data) {
        // Non-fatal (rows show raw keys), but say so rather than letting the fallback pass silently.
        toast.error("Failed to load spend categories");
      }
      setCategories(categoriesRes.data ?? []);
      setSummary(summaryRes.data);
    } catch {
      setError("Failed to load spend summary");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }

  // defaultCurrency first, then by descending total.
  const currencyRows = useMemo<SpendTotalsRow[]>(() => {
    const totals = summary?.totals ?? [];
    const def = summary?.defaultCurrency;
    return [...totals].sort((a, b) => {
      if (a.currency === def) return -1;
      if (b.currency === def) return 1;
      return num(b.total) - num(a.total);
    });
  }, [summary]);

  const partial = isPartialPeriod(summary?.to);
  const uncategorised = num(summary?.uncategorisedCount);

  return (
    <section className={cn("rounded-xl border bg-card/60 p-5 backdrop-blur-sm", className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Eyebrow>Payables</Eyebrow>
          <h2 className="mt-1 text-lg font-bold tracking-tight">Spend summary</h2>
          {summary ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatDate(summary.from, "short")} to {formatDate(summary.to, "short")}
              {partial && " · current period is incomplete (month to date)"}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter
            value={{ from: from ?? undefined, to: to ?? undefined }}
            onChange={(range) => {
              setFrom(range.from ?? null);
              setTo(range.to ?? null);
            }}
          />
          <Button size="sm" onClick={generate} disabled={loading || !from || !to}>
            {loading ? <Spinner className="size-4" /> : <Sparkles className="size-4" />}
            Generate
          </Button>
        </div>
      </div>

      <div className="mt-5 space-y-6">
        {error ? (
          <ErrorState message={error} onRetry={generate} />
        ) : loading && !summary ? (
          <LoadingState label="Working out your spend..." />
        ) : !summary ? (
          <EmptyState icon={BarChart3} title="See where the money goes">
            Pick a date range and generate a summary of committed and variable spend, by category, supplier
            and tag.
          </EmptyState>
        ) : (
          <div className={cn("space-y-6", loading && "opacity-60 transition-opacity")}>
            <CoverageCaveat
              uncategorised={uncategorised}
              mixedCurrency={summary.mixedCurrency === true}
            />
            {currencyRows.length === 0 ? (
              <EmptyState icon={BarChart3} title="No spend in this period">
                Try a wider date range.
              </EmptyState>
            ) : (
              currencyRows.map((totalsRow, i) => (
                <CurrencySection
                  key={totalsRow.currency}
                  totals={totalsRow}
                  categories={(summary.byCategory ?? []).filter((r) => r.currency === totalsRow.currency)}
                  suppliers={(summary.bySupplier ?? []).filter((r) => r.currency === totalsRow.currency)}
                  tags={(summary.tags ?? []).filter((r) => r.currency === totalsRow.currency)}
                  delta={(summary.delta ?? []).find((r) => r.currency === totalsRow.currency)}
                  partial={partial}
                  categoryName={categoryName}
                  onBillClick={onBillClick}
                  showDivider={i > 0}
                />
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function CoverageCaveat({
  uncategorised,
  mixedCurrency,
}: {
  uncategorised: number;
  mixedCurrency: boolean;
}) {
  if (uncategorised <= 0 && !mixedCurrency) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {uncategorised > 0 && (
        <Badge className="font-normal border-amber-500/25 bg-amber-500/15 text-amber-600">
          {uncategorised} line{uncategorised === 1 ? "" : "s"} uncategorised - classification incomplete
        </Badge>
      )}
      {mixedCurrency && (
        <Badge variant="outline" className="font-normal">
          Multiple currencies - shown separately, never combined
        </Badge>
      )}
    </div>
  );
}

function CurrencySection({
  totals,
  categories,
  suppliers,
  tags,
  delta,
  partial,
  categoryName,
  onBillClick,
  showDivider,
}: {
  totals: SpendTotalsRow;
  categories: SpendCategoryRow[];
  suppliers: SpendSupplierRow[];
  tags: SpendTagRow[];
  delta: SpendDeltaRow | undefined;
  partial: boolean;
  categoryName: (key: string | null | undefined) => string;
  onBillClick?: (billId: string) => void;
  showDivider: boolean;
}) {
  const currency = totals.currency;
  const fixedShare = pct(totals.fixedShare);

  return (
    <div className="space-y-5">
      {showDivider && <Separator />}

      <div>
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <Eyebrow>Total spend · {currency}</Eyebrow>
            <p className="mt-1 text-2xl font-bold tracking-tight tabular-nums">{formatCurrency(totals.total, currency)}</p>
          </div>
          <DeltaChip delta={delta} currency={currency} partial={partial} />
        </div>
        <FixedVariableBar
          fixed={num(totals.fixed)}
          variable={num(totals.variable)}
          fixedShare={fixedShare}
          currency={currency}
        />
      </div>

      {categories.length > 0 && (
        <div>
          <SectionLabel>By category</SectionLabel>
          <CategoryBars
            rows={categories}
            currency={currency}
            categoryName={categoryName}
            onBillClick={onBillClick}
          />
        </div>
      )}

      {suppliers.length > 0 && (
        <div>
          <SectionLabel>By supplier</SectionLabel>
          <SupplierTable rows={suppliers} currency={currency} onBillClick={onBillClick} />
        </div>
      )}

      {tags.length > 0 && (
        <div>
          <SectionLabel>Tags</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {tags
              .slice()
              .sort((a, b) => num(b.total) - num(a.total))
              .map((t) => (
                <Badge key={t.tag} variant="outline" className="font-normal">
                  {t.tag} · {formatCurrency(t.total, currency)}
                </Badge>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <Eyebrow className="mb-2">{children}</Eyebrow>;
}

function DeltaChip({
  delta,
  currency,
  partial,
}: {
  delta: SpendDeltaRow | undefined;
  currency: string;
  partial: boolean;
}) {
  if (!delta) return null;
  const dp = pct(delta.deltaPercent);
  const Icon =
    delta.direction === "up" ? TrendingUp : delta.direction === "down" ? TrendingDown : Minus;
  const label =
    dp == null
      ? `vs ${formatCurrency(delta.previousTotal, currency)} prior`
      : `${dp >= 0 ? "+" : ""}${dp}% vs prior period`;
  return (
    <div className="flex flex-col items-end gap-0.5 text-right">
      <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
        <Icon className="size-3" />
        {label}
      </span>
      {partial && <span className="text-[10px] text-amber-600">month to date, not comparable</span>}
    </div>
  );
}

function FixedVariableBar({
  fixed,
  variable,
  fixedShare,
  currency,
}: {
  fixed: number;
  variable: number;
  fixedShare: number | null;
  currency: string;
}) {
  const total = fixed + variable;
  if (total <= 0) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        No classified spend yet - fixed vs variable unavailable.
      </p>
    );
  }
  const fixedPct = (fixed / total) * 100;
  return (
    <div className="mt-3 space-y-1.5">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-accent-blue" style={{ width: `${fixedPct}%` }} />
        <div className="h-full bg-accent-blue/30" style={{ width: `${100 - fixedPct}%` }} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">{formatCurrency(fixed, currency)}</span> committed
          {fixedShare != null && ` · ${fixedShare}%`}
        </span>
        <span>
          <span className="font-medium text-foreground">{formatCurrency(variable, currency)}</span> variable
        </span>
      </div>
    </div>
  );
}

function CategoryBars({
  rows,
  currency,
  categoryName,
  onBillClick,
}: {
  rows: SpendCategoryRow[];
  currency: string;
  categoryName: (key: string | null | undefined) => string;
  onBillClick?: (billId: string) => void;
}) {
  const sorted = [...rows].sort((a, b) => num(b.total) - num(a.total));
  const max = Math.max(...sorted.map((r) => num(r.total)), 1);
  return (
    <div className="space-y-2.5">
      {sorted.map((row) => {
        const total = num(row.total);
        const fixed = num(row.fixed);
        const widthPct = (total / max) * 100;
        const fixedPct = total > 0 ? (fixed / total) * 100 : 0;
        const isAi = row.category === AI_CATEGORY_KEY;
        return (
          <DrillRow
            key={row.category ?? "__uncategorised"}
            billIds={row.billIds}
            billCount={num(row.billCount)}
            label={categoryName(row.category)}
            onBillClick={onBillClick}
          >
            <div className="mb-0.5 flex items-center justify-between gap-2 text-sm">
              <span
                className={cn(
                  "flex items-center gap-1.5 truncate",
                  isAi && "font-semibold text-violet-600",
                  row.category == null && "italic text-muted-foreground",
                )}
              >
                {isAi && <Sparkles className="size-3.5 shrink-0" />}
                {categoryName(row.category)}
              </span>
              <span className="shrink-0 tabular-nums">{formatCurrency(total, currency)}</span>
            </div>
            <div
              className={cn(
                "flex h-2.5 overflow-hidden rounded-full bg-muted",
                isAi && "ring-1 ring-violet-400/40",
              )}
              style={{ width: `${Math.max(widthPct, 2)}%` }}
            >
              <div className="h-full bg-accent-blue" style={{ width: `${fixedPct}%` }} />
              <div className="h-full bg-accent-blue/30" style={{ width: `${100 - fixedPct}%` }} />
            </div>
          </DrillRow>
        );
      })}
    </div>
  );
}

function SupplierTable({
  rows,
  currency,
  onBillClick,
}: {
  rows: SpendSupplierRow[];
  currency: string;
  onBillClick?: (billId: string) => void;
}) {
  const sorted = [...rows].sort((a, b) => num(b.total) - num(a.total));
  return (
    <div className="space-y-1">
      {sorted.map((row) => {
        const total = num(row.total);
        const variablePct = total > 0 ? Math.round((num(row.variable) / total) * 100) : null;
        return (
          <DrillRow
            key={row.supplierId ?? row.supplierName}
            billIds={row.billIds}
            billCount={num(row.billCount)}
            label={row.supplierName}
            onBillClick={onBillClick}
          >
            <div className="flex items-center justify-between gap-2 py-1 text-sm">
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="truncate font-medium">{row.supplierName}</span>
              </span>
              <span className="shrink-0 tabular-nums">
                {formatCurrency(total, currency)}
                {variablePct != null && (
                  <span className="ml-1.5 text-xs text-muted-foreground">{variablePct}% var</span>
                )}
              </span>
            </div>
          </DrillRow>
        );
      })}
    </div>
  );
}

/** `billIds` is capped at 50 server-side, so "+N more" is shown against the true `billCount`. */
function DrillRow({
  children,
  billIds,
  billCount,
  label,
  onBillClick,
}: {
  children: React.ReactNode;
  billIds: string[] | undefined;
  billCount: number;
  label: string;
  onBillClick?: (billId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ids = billIds ?? [];
  const interactive = !!onBillClick && ids.length > 0;

  if (!interactive) return <div>{children}</div>;

  const hiddenCount = billCount - ids.length;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-md text-left transition-colors hover:bg-muted/50"
      >
        <div className="flex items-center gap-1">
          <ChevronDown
            className={cn(
              "size-3.5 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
          <div className="flex-1">{children}</div>
        </div>
      </button>
      {open && (
        <div className="ml-5 mt-1 flex flex-wrap gap-1.5 pb-1">
          {ids.map((id, i) => (
            <button
              key={id}
              type="button"
              onClick={() => onBillClick!(id)}
              className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={`Open bill ${i + 1} for ${label}`}
            >
              Bill {i + 1}
            </button>
          ))}
          {hiddenCount > 0 && (
            <span className="px-1 py-0.5 text-xs text-muted-foreground">+{hiddenCount} more</span>
          )}
        </div>
      )}
    </div>
  );
}
